import React from 'react';

/* ── Design tokens (JS mirror of CSS vars) ── */
export const T = {
  bg:         '#e8e6e1',
  surface:    '#d8d6d0',
  ink:        '#1a1916',
  muted:      '#6b6860',
  accent:     '#C8A96E',
  accentInk:  '#ffffff',
  dark:       '#1a1916',
  darkInk:    '#e8e6e1',
};

/* ── Spinner ── */
export function Spinner({ size = 24, color = T.accent }) {
  return <div style={{ width: size, height: size, border: `2px solid ${T.surface}`, borderTopColor: color, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0 }} />;
}

/* ── Button — flat, square corners, decisive ── */
export function Button({ children, variant = 'primary', size = 'md', fullWidth, disabled, loading, onClick, type = 'button', style = {} }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    fontWeight: 500, borderRadius: 0, border: 'none', letterSpacing: '0.04em',
    textTransform: 'uppercase', cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1, width: fullWidth ? '100%' : undefined,
    transition: 'background 0.15s, color 0.15s, opacity 0.15s', fontFamily: 'inherit',
  };
  const sizes = {
    sm: { padding: '8px 18px', fontSize: '0.75rem' },
    md: { padding: '13px 28px', fontSize: '0.8125rem' },
    lg: { padding: '16px 36px', fontSize: '0.875rem' },
  };
  const variants = {
    primary:     { background: T.accent,   color: T.accentInk },
    dark:        { background: T.ink,      color: T.bg },
    outline:     { background: 'transparent', color: T.ink,    border: `1px solid ${T.ink}` },
    outlineGold: { background: 'transparent', color: T.accent, border: `1px solid ${T.accent}` },
    ghost:       { background: 'transparent', color: T.muted,  border: '1px solid transparent' },
    danger:      { background: '#991b1b',  color: '#fff' },
    success:     { background: '#14532d',  color: '#fff' },
    light:       { background: T.bg,       color: T.ink },
  };
  const hovers = { primary: '#b8943a', dark: '#333', outline: T.accent, outlineGold: T.accent, ghost: T.surface, danger: '#7f1d1d', success: '#052e16', light: T.surface };
  const v = variants[variant] || variants.primary;
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading}
      style={{ ...base, ...sizes[size], ...v, ...style }}
      onMouseEnter={e => { if (!disabled && !loading) { e.currentTarget.style.background = hovers[variant] || T.accent; if (variant === 'outline' || variant === 'outlineGold') { e.currentTarget.style.color = T.accentInk; } } }}
      onMouseLeave={e => { if (!disabled && !loading) { e.currentTarget.style.background = v.background; e.currentTarget.style.color = v.color; } }}>
      {loading ? <Spinner size={14} color={variant === 'outline' || variant === 'outlineGold' ? T.ink : T.bg} /> : children}
    </button>
  );
}

/* ── Input ── */
export function Input({ label, error, hint, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</label>}
      <input {...props} style={{ width: '100%', border: `1px solid ${error ? '#991b1b' : T.surface}`, borderRadius: 0, padding: '11px 14px', fontSize: 'var(--step-body)', color: T.ink, background: T.bg, outline: 'none', transition: 'border-color 0.15s', ...props.style }}
        onFocus={e => { e.target.style.borderColor = T.accent; }}
        onBlur={e => { e.target.style.borderColor = error ? '#991b1b' : T.surface; }} />
      {hint && !error && <span style={{ fontSize: 'var(--step-xs)', color: T.muted }}>{hint}</span>}
      {error && <span style={{ fontSize: 'var(--step-xs)', color: '#991b1b' }}>{error}</span>}
    </div>
  );
}

/* ── Select ── */
export function Select({ label, error, children, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <select {...props} style={{ width: '100%', border: `1px solid ${T.surface}`, borderRadius: 0, padding: '11px 36px 11px 14px', fontSize: 'var(--step-body)', color: T.ink, background: T.bg, appearance: 'none', cursor: 'pointer', outline: 'none', ...props.style }}
          onFocus={e => e.target.style.borderColor = T.accent}
          onBlur={e => e.target.style.borderColor = T.surface}>
          {children}
        </select>
        <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: T.muted, fontSize: 10 }}>▼</span>
      </div>
      {error && <span style={{ fontSize: 'var(--step-xs)', color: '#991b1b' }}>{error}</span>}
    </div>
  );
}

/* ── Textarea ── */
export function Textarea({ label, error, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 'var(--step-xs)', fontWeight: 500, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</label>}
      <textarea {...props} style={{ width: '100%', border: `1px solid ${T.surface}`, borderRadius: 0, padding: '11px 14px', fontSize: 'var(--step-body)', color: T.ink, background: T.bg, outline: 'none', resize: 'vertical', minHeight: 110, fontFamily: 'inherit', lineHeight: 1.6, ...props.style }}
        onFocus={e => e.target.style.borderColor = T.accent}
        onBlur={e => e.target.style.borderColor = T.surface} />
      {error && <span style={{ fontSize: 'var(--step-xs)', color: '#991b1b' }}>{error}</span>}
    </div>
  );
}

/* ── Badge ── */
export function Badge({ children, variant = 'default', style = {} }) {
  const map = {
    default:  { background: T.surface, color: T.muted },
    gold:     { background: T.accent,  color: T.accentInk },
    dark:     { background: T.ink,     color: T.bg },
    green:    { background: '#14532d', color: '#d1fae5' },
    red:      { background: '#7f1d1d', color: '#fee2e2' },
    uk:       { background: T.ink,     color: T.bg },
    sold:     { background: T.muted,   color: T.bg },
    reserved: { background: T.accent,  color: T.accentInk },
  };
  const v = map[variant] || map.default;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', fontSize: 'var(--step-xs)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', ...v, ...style }}>{children}</span>;
}

/* ── Modal ── */
export function Modal({ open, onClose, title, children, width = 560 }) {
  React.useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [open]);
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,25,22,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fade-up" style={{ background: T.bg, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', padding: '36px 40px', boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <h2 style={{ fontSize: 'var(--step-lead)', fontWeight: 400, fontFamily: 'Playfair Display, serif', lineHeight: 1.2 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, color: T.muted, cursor: 'pointer', lineHeight: 1, flexShrink: 0, marginLeft: 16 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Toast ── */
export function Toast({ message, type = 'success', onClose }) {
  React.useEffect(() => { const t = setTimeout(onClose, 3800); return () => clearTimeout(t); }, [onClose]);
  const colors = { success: T.ink, error: '#7f1d1d', info: T.muted };
  return (
    <div style={{ position: 'fixed', top: 80, right: 20, zIndex: 2000, background: colors[type], color: T.bg, padding: '14px 22px', fontSize: 'var(--step-sm)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.25)', maxWidth: 340, letterSpacing: '0.02em' }}>
      <span>{type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>{message}
    </div>
  );
}

export function useToast() {
  const [toasts, setToasts] = React.useState([]);
  const show = React.useCallback((message, type = 'success') => { const id = Date.now(); setToasts(t => [...t, { id, message, type }]); }, []);
  const remove = React.useCallback((id) => setToasts(t => t.filter(x => x.id !== id)), []);
  const ToastContainer = () => <div style={{ position: 'fixed', top: 80, right: 20, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 8 }}>{toasts.map(t => <Toast key={t.id} message={t.message} type={t.type} onClose={() => remove(t.id)} />)}</div>;
  return { show, ToastContainer };
}

/* ── Pagination ── */
export function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pages.push(i);
  const btn = (label, pg, disabled = false) => (
    <button key={label} onClick={() => !disabled && onChange(pg)} disabled={disabled}
      style={{ padding: '8px 14px', border: `1px solid ${pg === page ? T.ink : T.surface}`, background: pg === page ? T.ink : 'transparent', color: pg === page ? T.bg : T.ink, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 'var(--step-sm)', fontWeight: pg === page ? 600 : 400, opacity: disabled ? 0.35 : 1, transition: 'all 0.15s', fontFamily: 'inherit' }}>
      {label}
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', padding: '40px 0', flexWrap: 'wrap' }}>
      {btn('← Prev', page - 1, page === 1)}
      {pages[0] > 1 && <>{btn('1', 1)}{pages[0] > 2 && <span style={{ padding: '8px 6px', color: T.muted, fontSize: 'var(--step-sm)' }}>…</span>}</>}
      {pages.map(p => btn(String(p), p))}
      {pages[pages.length - 1] < totalPages && <>{pages[pages.length - 1] < totalPages - 1 && <span style={{ padding: '8px 6px', color: T.muted, fontSize: 'var(--step-sm)' }}>…</span>}{btn(String(totalPages), totalPages)}</>}
      {btn('Next →', page + 1, page === totalPages)}
    </div>
  );
}

/* ── EmptyState ── */
export function EmptyState({ icon = '○', title, message, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 20, color: T.muted }}>{icon}</div>
      <h3 style={{ fontSize: 'var(--step-lead)', fontFamily: 'Playfair Display, serif', fontWeight: 400, marginBottom: 12 }}>{title}</h3>
      {message && <p style={{ color: T.muted, fontSize: 'var(--step-body)', marginBottom: 24, maxWidth: '40ch', margin: '0 auto 24px' }}>{message}</p>}
      {action}
    </div>
  );
}

/* ── Helpers ── */
export function formatPrice(p)   { return `€${Number(p).toLocaleString('en-IE')}`; }
export function formatMileage(m) { return `${Number(m).toLocaleString('en-IE')} km`; }
export function formatDate(d)    { return new Date(d).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }); }
export function timeAgo(d) {
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today'; if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`; if (days < 30) return `${Math.floor(days/7)}w ago`;
  return formatDate(d);
}
